/** Durations stored in hours (an activity's visit duration, `DecimalField(5, 2)`): the
 *  record editor's duration widget and the catalog's words read them through these — the
 *  units of `formatTripDuration` ("1 h 30 min"). `catalog/words.ts` re-exports them (the
 *  One-Wording Rule: one function per wording). Pure. */

/** Stored hours → whole hours and minutes, rounded to the minute (1.5 → 1 h 30 min). */
export function splitHours(hours: number): { h: number; m: number } {
  const total = Math.round(hours * 60);
  return { h: Math.floor(total / 60), m: total % 60 };
}

/** Hours and minutes → the stored value (`DecimalField(5, 2)`, hours). Every whole minute
 *  survives the round trip: 0.005 h is 0.3 minutes. */
export function joinHours(h: number, m: number): number {
  return Math.round((h + m / 60) * 100) / 100;
}

const isHours = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0;

/** "1 h 30 min", "45 min", "2 h", "0 min" — the units of `formatTripDuration`; "—" when
 *  unknown. Hours keep counting past a day (the editor's hours field does too). */
export function formatHours(hours: number | null | undefined): string {
  if (!isHours(hours)) return '—';
  const { h, m } = splitHours(hours);
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** A visit's duration range: "1 h – 1 h 30 min"; one value when both ends read the same;
 *  "at least 1 h" / "up to 2 h" with one end; "—" with none. Shown as stored, even when
 *  the minimum is the longer (the editor flags that). */
export function formatHoursRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  const lo = isHours(min) ? formatHours(min) : null;
  const hi = isHours(max) ? formatHours(max) : null;
  if (lo && hi) return lo === hi ? lo : `${lo} – ${hi}`;
  if (lo) return `at least ${lo}`;
  if (hi) return `up to ${hi}`;
  return '—';
}
